/*!
 * Custom product slider
 *
 * Doua moduri, alese de CSS, nu de JS:
 *  - mobil (< 750px): track-ul e scroll container nativ cu scroll-snap. JS-ul nu
 *    atinge transform-ul, doar traduce click-ul pe sageti in `scrollBy` si tine
 *    starea butoanelor. Swipe-ul, momentum-ul si focusul le face browserul.
 *  - desktop/tableta: track mutat cu `transform`, cu drag prin Pointer Events.
 *
 * Modul se detecteaza din `overflow-x`, deci nu exista praguri duplicate in JS.
 * Gap-ul si latimea cardului se citesc tot din layout-ul real, ca sa nu se poata
 * desincroniza de CSS.
 */
(function () {
  const SELECTOR = '[data-custom-product-slider]';
  const DRAG_THRESHOLD = 5;

  // Folosite doar pentru cate carduri intra intr-o fereastra, nu pentru layout.
  // Pragurile trebuie sa ramana identice cu cele din section-custom-product-slider.css.
  const MOBILE_SMALL_MAX = 499;
  const MOBILE_MAX = 749;
  const TABLET_MAX = 989;

  class CustomProductSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.custom-product-slider__track');
      this.container = root.querySelector('.custom-product-slider__container');
      this.prevBtn = root.querySelector('.custom-product-slider__nav--prev');
      this.nextBtn = root.querySelector('.custom-product-slider__nav--next');
      this.items = Array.from(root.querySelectorAll('.custom-product-slider__item'));

      if (!this.track || !this.items.length) return;

      this.perView = {
        mobile: parseInt(root.dataset.itemsMobile, 10) || 1,
        mobileLarge: parseInt(root.dataset.itemsMobileLg, 10) || 2,
        tablet: parseInt(root.dataset.itemsTablet, 10) || 2,
        desktop: parseInt(root.dataset.itemsDesktop, 10) || 4,
      };

      this.currentIndex = 0;
      this.isDragging = false;
      this.wasDragged = false;
      this.dragDelta = 0;
      this.scrollRaf = null;

      this.onPointerMove = this.onPointerMove.bind(this);
      this.endDrag = this.endDrag.bind(this);

      this.bind();
      this.update();
    }

    // Scroll nativ cat timp CSS-ul face track-ul scrollabil pe orizontala.
    get isNative() {
      const overflowX = getComputedStyle(this.track).overflowX;
      return overflowX === 'auto' || overflowX === 'scroll';
    }

    get itemsToShow() {
      const w = window.innerWidth;
      if (w <= MOBILE_SMALL_MAX) return this.perView.mobile;
      if (w <= MOBILE_MAX) return this.perView.mobileLarge;
      if (w <= TABLET_MAX) return this.perView.tablet;
      return this.perView.desktop;
    }

    get stepSize() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return this.items[0].offsetWidth + gap;
    }

    get viewportWidth() {
      return this.track.clientWidth;
    }

    get maxOffset() {
      return Math.max(0, this.track.scrollWidth - this.viewportWidth);
    }

    bind() {
      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.container);
      } else {
        window.addEventListener('resize', () => this.update());
      }

      // In mod nativ starea butoanelor urmareste scroll-ul real.
      this.track.addEventListener(
        'scroll',
        () => {
          if (!this.isNative) return;
          if (this.scrollRaf) return;
          this.scrollRaf = requestAnimationFrame(() => {
            this.scrollRaf = null;
            this.updateButtons();
          });
        },
        { passive: true }
      );

      this.track.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.track.addEventListener('dragstart', (event) => event.preventDefault());

      this.track.addEventListener(
        'click',
        (event) => {
          if (!this.wasDragged) return;
          event.preventDefault();
          event.stopPropagation();
          this.wasDragged = false;
        },
        true
      );
    }

    step(direction) {
      if (this.isNative) {
        this.track.scrollBy({ left: direction * this.stepSize, behavior: 'smooth' });
        return;
      }
      this.goTo(this.currentIndex + direction);
    }

    goTo(index) {
      const maxIndex = Math.max(0, this.items.length - this.itemsToShow);
      this.currentIndex = Math.max(0, Math.min(index, maxIndex));
      this.update();
    }

    update() {
      if (this.isNative) {
        // Curatam urmele modului cu transform, in caz ca s-a trecut prin resize.
        if (this.track.style.transform) this.track.style.transform = '';
        this.clearInert();
        this.updateButtons();
        return;
      }

      const offset = Math.min(this.currentIndex * this.stepSize, this.maxOffset);
      this.track.style.transform = `translateX(-${offset}px)`;
      this.updateButtons(offset);
      this.updateInert(offset);
    }

    updateButtons(offset) {
      if (this.isNative) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 1;
        if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max - 1;
        return;
      }

      const current = offset ?? Math.min(this.currentIndex * this.stepSize, this.maxOffset);
      if (this.prevBtn) this.prevBtn.disabled = current <= 0;
      if (this.nextBtn) this.nextBtn.disabled = current >= this.maxOffset - 1;
    }

    // Doar in modul cu transform: cardurile din afara ferestrei nu trebuie sa
    // primeasca focus, pentru ca nu exista scroll care sa le aduca pe ecran.
    updateInert(offset) {
      const viewLeft = offset;
      const viewRight = offset + this.viewportWidth;

      this.items.forEach((item) => {
        const left = item.offsetLeft;
        const visible = left + item.offsetWidth > viewLeft + 1 && left < viewRight - 1;
        item.toggleAttribute('inert', !visible);
        item.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
    }

    clearInert() {
      this.items.forEach((item) => {
        item.removeAttribute('inert');
        item.removeAttribute('aria-hidden');
      });
    }

    startDrag(event) {
      if (this.isNative) return;
      if (event.button && event.button !== 0) return;

      this.isDragging = true;
      this.wasDragged = false;
      this.dragDelta = 0;
      this.dragStartX = event.clientX;
      this.dragStartOffset = -Math.min(this.currentIndex * this.stepSize, this.maxOffset);

      this.track.classList.add('is-dragging');
      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.endDrag);
      document.addEventListener('pointercancel', this.endDrag);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      this.dragDelta = event.clientX - this.dragStartX;
      if (Math.abs(this.dragDelta) > DRAG_THRESHOLD) this.wasDragged = true;
      this.track.style.transform = `translateX(${this.dragStartOffset + this.dragDelta}px)`;
    }

    endDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');

      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.endDrag);
      document.removeEventListener('pointercancel', this.endDrag);

      const movedSlides = Math.round(-this.dragDelta / this.stepSize);
      this.goTo(this.currentIndex + movedSlides);
    }
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach((el) => {
      if (el.dataset.sliderReady === 'true') return;
      el.dataset.sliderReady = 'true';
      new CustomProductSlider(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
